import { SurveyQuestionType, surveyQuestionTypeByQuestionRef } from './appModel';
import clientIntakeFormSchema from './utils/clientIntakeFormSchema.json';

export const QUICK_EDIT_PROFILE_FIELDS = [
  'clientFirstName',
  'clientLastName',
] as const;

export const QUICK_EDIT_PREDICTION_FIELDS = [
  'clientBirthDate',
  'clientZipCode',
  'clientLtcZipCode',
  'clientHasDifferentLtcZipCode',
  'clientGenderAtBirth',
  'clientRace',
  'clientHeightInches',
  'clientWeightPounds',
  'clientDisabilityWorkersComp',
  'householdTotalAssetsValue',
  'householdHouseholdSize',
  'householdLivingChildrenCount',
  'clientLivingSiblingsCount',
] as const;

export const QUICK_EDIT_ALL_FIELDS = [
  ...QUICK_EDIT_PROFILE_FIELDS,
  ...QUICK_EDIT_PREDICTION_FIELDS,
] as const;

export type QuickEditFieldName = (typeof QUICK_EDIT_ALL_FIELDS)[number];

/**
 * The exact values the intake stores for the three multiple-choice quick-edit fields. The app's
 * quick-edit form offers these and nothing else, and the prediction models read them verbatim,
 * so a write of "female" or "Asian" is not a spelling variant: it is a value no model knows.
 */
export const QUICK_EDIT_GENDER_OPTIONS = [
  'Male',
  'Female',
  'Intersex',
  "I don't know / prefer not to answer",
] as const;

export const QUICK_EDIT_RACE_OPTIONS = [
  'White or European (e.g., German, Irish, English, Italian, Polish, French, etc.)',
  'Asian or Asian American (e.g., Chinese, Japanese, Filipino, Korean, South Asian, Vietnamese, etc.)',
  'Hispanic or Latino/a (e.g., Puerto Rican, Mexican, Cuban, Salvadoran, Colombian, etc.)',
  'Black or African American (e.g., Jamaican, Nigerian, Haitian, Ethiopian, etc.)',
  'Middle Eastern or North African (e.g., Lebanese, Iranian, Egyptian, Moroccan, Israeli, Palestinian, etc.)',
  'Native Hawaiian or Pacific Islander (e.g., Samoan, Guamanian, Chamorro, Tongan, etc.)',
  'American Indian or Alaska Native (e.g., Navajo Nation, Blackfeet Tribe, Inupiat Traditional Government, etc.)',
  "I don't know / prefer not to answer",
] as const;

export const QUICK_EDIT_DISABILITY_OPTIONS = ['Yes', 'No', "I don't know / prefer not to answer"] as const;

/**
 * A field as a person reads it: "Birth Date", not `clientBirthDate`.
 *
 * Taken from the intake form's own schema, which is where the app's quick-edit screen gets its
 * labels, so an advisor reading a change back by email sees the same words as on the page. A ref
 * with no schema entry falls back to its own name rather than throwing — a label is presentation,
 * and a missing one must not stop a correction being made.
 */
export function quickEditFieldLabel(field: string): string {
  const schema = clientIntakeFormSchema as Record<string, { label?: string } | undefined>;
  return schema[field]?.label ?? field;
}

/** The ranges the app's quick-edit form enforces, so a write from anywhere else cannot exceed them. */
export const QUICK_EDIT_HEIGHT_INCHES = { min: 48, max: 84 } as const;
export const QUICK_EDIT_HOUSEHOLD_COUNT = { min: 0, max: 50 } as const;
const WEIGHT_POUNDS = { min: 50, max: 700 } as const;
const ASSETS_MAX = 10_000_000_000;

/**
 * Turning a value someone typed into the type its survey question is stored as.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS
 *
 * `updateSurveyAnswersSql` hands the value straight to the formatter for the field's question
 * type, and every one of those formatters assumes it already has the right JavaScript type:
 * `dbFormatDate` calls `.toISOString()`, `dbFormatInt` calls `Math.trunc()`, `dbFormatBoolean`
 * tests truthiness. Nothing on either write path supplied one, so a value that arrived as a
 * string — which is all JSON can carry — behaved like this:
 *
 *   clientBirthDate      "1950-03-03"  threw `value.toISOString is not a function`
 *   clientBirthDate      "2026-02-31"  rolled over to 3 March, silently
 *   clientHeightInches   "5 ft 10"     wrote the literal string `NaN`
 *
 * The last two are the dangerous ones: the write succeeds, and a prediction field then
 * recalculates every projection the advisor is looking at from a date or a number that is not
 * the one anybody typed.
 *
 * The boolean branch has no quick-edit field behind it today — `clientHasDifferentLtcZipCode`
 * reads like one but is absent from `surveyQuestionTypeByQuestionRef`, so it falls to text and
 * keeps storing a string. It is here because `dbFormatBoolean` tests truthiness, so the day a
 * boolean-typed field joins the allowlist, "false" would be written as `t`.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────
 * IT REFUSES RATHER THAN GUESSES
 *
 * A failure returns a reason instead of throwing, so each caller can answer in its own voice —
 * the conversational API turns it into an `INVALID_INPUT` the agent can read and act on.
 *
 * Dates must be ISO (`YYYY-MM-DD`). `3/4/1950` is deliberately refused: it is 3 April to one
 * person and 4 March to another, and a birth date silently shifted by nine months reprices the
 * whole plan. Asking is cheap; guessing is not.
 */

export type QuickEditValue = string | number | boolean | Date | null;

/**
 * One shape rather than a discriminated union: the server package compiles without
 * `strictNullChecks`, where narrowing on an `ok` discriminant does not hold, and a caller that
 * silently reads `value` off a failure is exactly the bug this function exists to stop.
 */
export interface QuickEditCoercion {
  /** The value to write. Meaningless unless `reason` is null. */
  value: QuickEditValue;
  /** Why it cannot be stored as this field's type, or null when it can. */
  reason: string | null;
}

/** ISO calendar date. Anything else is ambiguous across locales — see the header. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:T.*)?$/;

/** A whole number as a person writes one: `70`, `1,200`, `$250,000`. */
const WRITTEN_INTEGER = /^-?\d+$/;

const TRUE_WORDS = new Set(['true', 'yes', 'y', '1']);
const FALSE_WORDS = new Set(['false', 'no', 'n', '0']);

export function coerceQuickEditValue(
  field: QuickEditFieldName,
  value: string | number | boolean | Date | null,
): QuickEditCoercion {
  // Clearing a field is deliberate and stays allowed — see `UpdateIntakeSchema`, which refuses
  // an empty string for exactly this reason but lets an explicit null through.
  if (value === null) return { value: null, reason: null };

  const typed = coerceByQuestionType(field, readAsWritten(field, value));
  return typed.reason ? typed : validateForField(field, typed.value);
}

function coerceByQuestionType(
  field: QuickEditFieldName,
  value: string | number | boolean | Date,
): QuickEditCoercion {
  const questionType = surveyQuestionTypeByQuestionRef[field] ?? SurveyQuestionType.text;

  switch (questionType) {
    case SurveyQuestionType.date:
      return coerceDate(field, value);

    case SurveyQuestionType.integer:
      return coerceInteger(field, value);

    case SurveyQuestionType.boolean:
      return coerceBoolean(field, value);

    default:
      return coerceText(field, value);
  }
}

/**
 * Turns the way a person writes a height, weight or amount into the number the field stores:
 * `5'6"` / `5 ft 6 in` → 66, `165 lbs` → 165, `$1.25M` / `250k` → 1250000 / 250000. Anything
 * unrecognised passes through unchanged, for the type check to accept or refuse. Done here rather
 * than by whoever is typing, because a model converting feet to inches is arithmetic nobody checks.
 */
function readAsWritten(
  field: QuickEditFieldName,
  value: string | number | boolean | Date,
): string | number | boolean | Date {
  if (typeof value !== 'string') return value;
  const text = value.trim().toLowerCase();

  if (field === 'clientHeightInches') {
    const feetInches = text.match(
      /^(\d)\s*(?:'|ft\.?|feet|foot)\s*(?:(\d{1,2})\s*(?:"|''|in\.?|inches|inch)?)?$/,
    );
    if (feetInches) return Number(feetInches[1]) * 12 + Number(feetInches[2] ?? 0);
    const inches = text.match(/^(\d{2,3})\s*(?:"|in\.?|inches|inch)$/);
    if (inches) return Number(inches[1]);
  }

  if (field === 'clientWeightPounds') {
    const pounds = text.match(/^(\d{2,3})\s*(?:lbs?\.?|pounds?)$/);
    if (pounds) return Number(pounds[1]);
  }

  if (field === 'householdTotalAssetsValue') {
    const scaled = text
      .replace(/[\s,$]/g, '')
      .match(/^(\d+(?:\.\d+)?)(k|thousand|m|mm|million|b|billion)$/);
    if (scaled) {
      const unit = scaled[2];
      const multiplier =
        unit === 'k' || unit === 'thousand' ? 1_000 : unit.startsWith('b') ? 1_000_000_000 : 1_000_000;
      return Math.round(Number(scaled[1]) * multiplier);
    }
  }

  return value;
}

const DONT_KNOW = "I don't know / prefer not to answer";
const DONT_KNOW_ALIASES: Record<string, string> = {
  "i don't know": DONT_KNOW,
  unknown: DONT_KNOW,
  'prefer not to answer': DONT_KNOW,
  'prefer not to say': DONT_KNOW,
  declined: DONT_KNOW,
};

/**
 * A written choice as its stored option: an alias, the exact option in any case, or exactly one of
 * the option's named parts — "Asian", "Black", "Pacific Islander", "Alaska Native" — never merely
 * the start of one. "American" once matched "American Indian or Alaska Native" by prefix; on a
 * protected characteristic a guess is worse than asking, so partial text matches nothing and is
 * refused with the list of options.
 */
function matchOption(
  options: readonly string[],
  written: string,
  aliases: Record<string, string> = {},
): string | undefined {
  const text = written.trim().toLowerCase();
  if (!text) return undefined;
  if (aliases[text]) return aliases[text];

  const exact = options.find(option => option.toLowerCase() === text);
  if (exact) return exact;

  const named = options.filter(option => {
    const name = option.toLowerCase().split(' (')[0];
    return name === text || name.split(/\s+or\s+/).some(part => part.trim() === text);
  });
  return named.length === 1 ? named[0] : undefined;
}

function refuse(field: string, reason: string): QuickEditCoercion {
  return { value: null, reason: `${field} ${reason}` };
}

function inRange(
  field: string,
  value: QuickEditValue,
  min: number,
  max: number,
  unit: string,
): QuickEditCoercion {
  const number = value as number;
  return number >= min && number <= max
    ? { value, reason: null }
    : refuse(field, `must be between ${min} and ${max}${unit ? ' ' + unit : ''} — not ${number}.`);
}

/** The rules the app's quick-edit form applies, applied to every writer. */
function validateForField(field: QuickEditFieldName, value: QuickEditValue): QuickEditCoercion {
  switch (field) {
    case 'clientZipCode':
    case 'clientLtcZipCode': {
      const zip = String(value).trim().match(/^(\d{5})(?:-\d{4})?$/);
      return zip
        ? { value: zip[1], reason: null }
        : refuse(field, `must be a 5-digit zip code, such as 94103 — not ${String(value)}.`);
    }

    case 'clientGenderAtBirth': {
      const option = matchOption(QUICK_EDIT_GENDER_OPTIONS, String(value), {
        m: 'Male',
        f: 'Female',
        man: 'Male',
        woman: 'Female',
        ...DONT_KNOW_ALIASES,
      });
      return option
        ? { value: option, reason: null }
        : refuse(field, `must be one of: ${QUICK_EDIT_GENDER_OPTIONS.join('; ')}.`);
    }

    case 'clientRace': {
      const option = matchOption(QUICK_EDIT_RACE_OPTIONS, String(value), {
        caucasian: QUICK_EDIT_RACE_OPTIONS[0],
        latino: QUICK_EDIT_RACE_OPTIONS[2],
        latina: QUICK_EDIT_RACE_OPTIONS[2],
        'african american': QUICK_EDIT_RACE_OPTIONS[3],
        ...DONT_KNOW_ALIASES,
      });
      return option
        ? { value: option, reason: null }
        : refuse(
            field,
            `must be one of: ${QUICK_EDIT_RACE_OPTIONS.map(option => option.split(' (')[0]).join('; ')}.`,
          );
    }

    case 'clientDisabilityWorkersComp': {
      const option = matchOption(QUICK_EDIT_DISABILITY_OPTIONS, String(value), {
        y: 'Yes',
        n: 'No',
        true: 'Yes',
        false: 'No',
        ...DONT_KNOW_ALIASES,
      });
      return option
        ? { value: option, reason: null }
        : refuse(field, "must be Yes, No, or I don't know / prefer not to answer.");
    }

    case 'clientHeightInches':
      return inRange(
        field,
        value,
        QUICK_EDIT_HEIGHT_INCHES.min,
        QUICK_EDIT_HEIGHT_INCHES.max,
        'inches (4 ft 0 in to 7 ft 0 in)',
      );

    case 'clientWeightPounds':
      return inRange(field, value, WEIGHT_POUNDS.min, WEIGHT_POUNDS.max, 'pounds');

    case 'householdHouseholdSize':
    case 'householdLivingChildrenCount':
    case 'clientLivingSiblingsCount':
      return inRange(field, value, QUICK_EDIT_HOUSEHOLD_COUNT.min, QUICK_EDIT_HOUSEHOLD_COUNT.max, '');

    case 'householdTotalAssetsValue':
      return inRange(field, value, 0, ASSETS_MAX, 'dollars');

    case 'clientBirthDate': {
      const date = value as Date;
      const now = new Date();
      return date.getTime() > now.getTime() || date.getUTCFullYear() < now.getUTCFullYear() - 120
        ? refuse(field, `is not a plausible date of birth: ${date.toISOString().slice(0, 10)}.`)
        : { value, reason: null };
    }

    default:
      return { value, reason: null };
  }
}

function coerceDate(field: string, value: string | number | boolean | Date): QuickEditCoercion {
  // The API entrypoint (`api.ts`'s `recurseObject`/`fastMaybeParseDateString`) revives any
  // ISO-shaped string in a request body — including a bare `YYYY-MM-DD` — into a `Date` before
  // this ever runs, so a request coming through the HTTP quick-edit endpoint arrives as a `Date`,
  // not a string. The conversational `updateIntake` caller runs in-process and never passes
  // through that entrypoint, so it still sends a raw string. Accept both, same as
  // `sanitizeAdvisorPrefill.ts` already has to for this exact reason.
  const asString =
    value instanceof Date
      ? Number.isNaN(value.getTime())
        ? null
        : value.toISOString()
      : typeof value === 'string'
        ? value
        : null;

  if (asString === null || !ISO_DATE.test(asString.trim())) {
    return {
      value: null,
      reason: `${field} must be a date written as YYYY-MM-DD, such as 1950-03-03.`,
    };
  }

  const written = asString.trim();
  const date = new Date(written);
  if (Number.isNaN(date.getTime())) {
    return { value: null, reason: `${field} is not a real date: ${written}.` };
  }

  // A day that does not exist does not fail to parse — it ROLLS OVER. `new Date('2026-02-31')`
  // is 3 March, so a typed 31 becomes a different date with nothing to say it happened. Reading
  // the date back out and comparing is the only way to catch it.
  if (date.toISOString().slice(0, 10) !== written.slice(0, 10)) {
    return { value: null, reason: `${field} is not a real date: ${written}.` };
  }

  return { value: date, reason: null };
}

function coerceInteger(field: string, value: string | number | boolean | Date): QuickEditCoercion {
  if (typeof value === 'number') {
    // Refused, not truncated: 165.5 saved as 165 silently changed the client's data, while the
    // same value written as a string was already refused.
    return Number.isInteger(value)
      ? { value, reason: null }
      : { value: null, reason: `${field} must be a whole number, such as 70 — not ${value}.` };
  }

  if (typeof value === 'string') {
    // Commas, currency and spacing are how people write figures, not a different value.
    const digits = value.replace(/[\s,$]/g, '');
    if (WRITTEN_INTEGER.test(digits)) return { value: Number(digits), reason: null };
  }

  return {
    value: null,
    reason: `${field} must be a whole number, such as 70 — not ${String(value)}.`,
  };
}

function coerceBoolean(field: string, value: string | number | boolean | Date): QuickEditCoercion {
  if (typeof value === 'boolean') return { value, reason: null };

  const word = String(value).trim().toLowerCase();
  if (TRUE_WORDS.has(word)) return { value: true, reason: null };
  if (FALSE_WORDS.has(word)) return { value: false, reason: null };

  return { value: null, reason: `${field} must be yes or no.` };
}

function coerceText(field: string, value: string | number | boolean | Date): QuickEditCoercion {
  const text = typeof value === 'string' ? value.trim() : String(value);

  return text === ''
    ? { value: null, reason: `${field} needs a value.` }
    : { value: text, reason: null };
}
